/**
 * Frame registrations list + numeric transform editor (spec 2026-07-27-08 §6).
 *
 * Direct manipulation and anchor-pair calibration live in the plan editor
 * (spec 2026-08-22-03 §1/§6); this panel stays as the **precise fallback** —
 * type the exact numbers — and hands off to the editor through a "Calibrate…"
 * deep link (`?calibrate=<registrationUid>`) whenever the frame asset is one
 * the editor can open.
 *
 * Units are millimetres, Y-down: a positive rotation is clockwise on screen.
 */
import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { mdiCrosshairs, mdiTrashCanOutline } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { $api } from '../../api'
import { queryKeys, useAsset, useAssetTypes } from '../../hooks/useQueries'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog'
import { EmptyState } from '../ui/empty-state'
import { lotBaseTypeCode } from '../../lib/asset-type-tree'

const REGISTRATIONS_PATH = '/api/v1/organizations/{orgCode}/assets/{assetUid}/frame/registrations'
/** Asset types the plan editor can open — the deep link is hidden elsewhere. */
const EDITABLE_TYPE_CODES = ['floor', 'unit']

interface FrameRegistrationsPanelProps {
    org: string
    assetUid: string
}

interface TransformDraft {
    tx: string
    ty: string
    rotation_deg: string
    scale: string
}

function toDraft(transform: Record<string, unknown> | undefined): TransformDraft {
    return {
        tx: String(transform?.tx ?? 0),
        ty: String(transform?.ty ?? 0),
        rotation_deg: String(transform?.rotation_deg ?? 0),
        scale: String(transform?.scale ?? 1),
    }
}

export function FrameRegistrationsPanel({ org, assetUid }: FrameRegistrationsPanelProps) {
    const { t } = useTranslation('assets')
    const queryClient = useQueryClient()
    const [editingUid, setEditingUid] = useState<string | null>(null)
    const [draft, setDraft] = useState<TransformDraft>(toDraft(undefined))
    const [error, setError] = useState<string | null>(null)
    // The registration pending deletion (null = the confirmation dialog is closed).
    const [pendingDelete, setPendingDelete] = useState<{ uid: string, label: string } | null>(null)

    const listQuery = $api.useQuery('get', REGISTRATIONS_PATH, {
        params: { path: { orgCode: org, assetUid } },
    })

    // Both queries are already in cache on the asset page; they only decide
    // whether the editor deep link is offered.
    const { data: asset } = useAsset(org, assetUid)
    const { data: assetTypes = [] } = useAssetTypes(org, true)
    const frameTypeCode = asset?.asset_type_uid
        ? assetTypes.find((type) => type.uid === asset.asset_type_uid)?.code
        : undefined
    const editorAvailable = !!frameTypeCode && EDITABLE_TYPE_CODES.includes(lotBaseTypeCode(frameTypeCode))

    const invalidate = () => {
        queryClient.invalidateQueries({
            predicate: (query) => {
                const key = query.queryKey as unknown[]
                if (key[0] === 'get' && key[1] === REGISTRATIONS_PATH) return true
                // The composite render depends on every registration.
                return key[0] === 'composite-plan' && key[1] === org && key[2] === assetUid
            },
        })
        // Creating the first registration (or deleting the last one) flips
        // the asset's `composite_plan` flag, which gates the tab itself —
        // refresh the asset-detail query so the tab strip updates without a
        // reload (spec 2026-08-01-01).
        queryClient.invalidateQueries({ queryKey: queryKeys.asset(org, assetUid) })
    }

    const updateMutation = $api.useMutation('patch', '/api/v1/organizations/{orgCode}/frame-registrations/{registrationUid}', {
        onSuccess: () => {
            setEditingUid(null)
            setError(null)
            invalidate()
        },
        onError: () => setError(t('compositePlan.save_error')),
    })

    // Registrations are rows users create, redo and audit — "redo" is
    // delete + recreate, so a delete affordance is part of v1.
    const deleteMutation = $api.useMutation('delete', '/api/v1/organizations/{orgCode}/frame-registrations/{registrationUid}', {
        onSuccess: (_data, variables) => {
            const uid = variables.params.path.registrationUid
            setPendingDelete(null)
            setError(null)
            if (editingUid === uid) setEditingUid(null)
            invalidate()
        },
        onError: () => {
            setPendingDelete(null)
            setError(t('compositePlan.delete_error'))
        },
    })

    const registrations = listQuery.data?.registrations ?? []

    const startEdit = (uid: string, transform: Record<string, unknown> | undefined) => {
        setEditingUid(uid)
        setDraft(toDraft(transform))
        setError(null)
    }

    const save = (uid: string) => {
        const numeric = {
            tx: Number(draft.tx),
            ty: Number(draft.ty),
            rotation_deg: Number(draft.rotation_deg),
            scale: Number(draft.scale),
        }
        if (Object.values(numeric).some((v) => !Number.isFinite(v)) || numeric.scale <= 0) {
            setError(t('compositePlan.invalid_transform'))
            return
        }
        updateMutation.mutate({
            params: { path: { orgCode: org, registrationUid: uid } },
            body: { transform: numeric },
        })
    }

    return (
        <div className="bg-card rounded-lg border border-border" data-testid="frame-registrations">
            <div className="border-b border-border px-4 py-3">
                <h3 className="text-sm font-medium">{t('compositePlan.registrations_title')}</h3>
                <p className="text-xs text-muted-foreground">{t('compositePlan.registrations_hint')}</p>
            </div>

            {listQuery.isLoading && (
                <div className="px-4 py-6 text-sm text-muted-foreground" data-testid="frame-registrations-loading">
                    {t('compositePlan.loading')}
                </div>
            )}

            {!listQuery.isLoading && registrations.length === 0 && (
                <EmptyState
                    variant="inline"
                    title={t('compositePlan.registrations_empty')}
                    data-testid="frame-registrations-empty"
                />
            )}

            <ul className="divide-y divide-border">
                {registrations.map((reg) => {
                    const transform = (reg.transform ?? {}) as Record<string, unknown>
                    const isEditing = editingUid === reg.uid
                    const label = reg.source_name
                        || (reg.source_representation_uid
                            ? t('compositePlan.source_representation', { kind: reg.source_representation_kind ?? '' })
                            : t('compositePlan.source_frame'))
                    return (
                        <li key={reg.uid} className="px-4 py-3" data-testid={`frame-registration-row-${reg.uid}`}>
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <div className="min-w-0">
                                    <p className="truncate text-sm font-medium">{label}</p>
                                    <p className="text-xs text-muted-foreground">
                                        {t('compositePlan.transform_summary', {
                                            tx: Number(transform.tx ?? 0),
                                            ty: Number(transform.ty ?? 0),
                                            rotation: Number(transform.rotation_deg ?? 0),
                                            scale: Number(transform.scale ?? 1),
                                        })}
                                    </p>
                                </div>
                                <div className="flex items-center gap-2">
                                    {editorAvailable && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            asChild
                                            title={t('compositePlan.calibrate_hint')}
                                            data-testid={`frame-registration-calibrate-${reg.uid}`}
                                        >
                                            <Link
                                                to="/$org/assets/$assetUid/plan-editor"
                                                params={{ org, assetUid }}
                                                search={{ calibrate: reg.uid }}
                                            >
                                                <AppIcon path={mdiCrosshairs} size="sm" className="mr-1" />
                                                {t('compositePlan.calibrate')}
                                            </Link>
                                        </Button>
                                    )}
                                    {!isEditing && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            data-testid={`frame-registration-edit-${reg.uid}`}
                                            onClick={() => startEdit(reg.uid, transform)}
                                        >
                                            {t('compositePlan.edit_transform')}
                                        </Button>
                                    )}
                                    <Button
                                        variant="destructive"
                                        size="icon"
                                        aria-label={t('compositePlan.delete_registration')}
                                        title={t('compositePlan.delete_registration')}
                                        data-testid={`frame-registration-delete-${reg.uid}`}
                                        onClick={() => { setError(null); setPendingDelete({ uid: reg.uid, label }) }}
                                    >
                                        <AppIcon path={mdiTrashCanOutline} size="sm" />
                                    </Button>
                                </div>
                            </div>

                            {isEditing && (
                                <div className="mt-3 space-y-3" data-testid={`frame-registration-editor-${reg.uid}`}>
                                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                                        {([
                                            ['tx', t('compositePlan.field_tx')],
                                            ['ty', t('compositePlan.field_ty')],
                                            ['rotation_deg', t('compositePlan.field_rotation')],
                                            ['scale', t('compositePlan.field_scale')],
                                        ] as Array<[keyof TransformDraft, string]>).map(([field, fieldLabel]) => (
                                            <div key={field} className="space-y-1">
                                                <Label htmlFor={`transform-${field}-${reg.uid}`} className="text-xs">{fieldLabel}</Label>
                                                <Input
                                                    id={`transform-${field}-${reg.uid}`}
                                                    type="number"
                                                    step="any"
                                                    value={draft[field]}
                                                    data-testid={`frame-registration-${field}`}
                                                    onChange={(e) => setDraft((d) => ({ ...d, [field]: e.target.value }))}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                    {error && (
                                        <p className="text-xs text-destructive" data-testid="frame-registration-error">{error}</p>
                                    )}
                                    <div className="flex gap-2">
                                        <Button
                                            size="sm"
                                            disabled={updateMutation.isPending}
                                            data-testid="frame-registration-save"
                                            onClick={() => save(reg.uid)}
                                        >
                                            {t('compositePlan.save')}
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            data-testid="frame-registration-cancel"
                                            onClick={() => { setEditingUid(null); setError(null) }}
                                        >
                                            {t('compositePlan.cancel')}
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </li>
                    )
                })}
            </ul>

            {error && !editingUid && (
                <p className="px-4 pb-3 text-xs text-destructive" data-testid="frame-registration-list-error">{error}</p>
            )}

            <AlertDialog open={pendingDelete !== null} onOpenChange={(open) => { if (!open) setPendingDelete(null) }}>
                <AlertDialogContent data-testid="frame-registration-delete-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('compositePlan.delete_title')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('compositePlan.delete_description', { name: pendingDelete?.label ?? '' })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="frame-registration-delete-cancel">
                            {t('compositePlan.cancel')}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="frame-registration-delete-confirm"
                            disabled={deleteMutation.isPending}
                            onClick={(e) => {
                                // Keep the dialog mounted until the request settles,
                                // so a failure can surface instead of vanishing.
                                e.preventDefault()
                                if (!pendingDelete) return
                                deleteMutation.mutate({
                                    params: { path: { orgCode: org, registrationUid: pendingDelete.uid } },
                                })
                            }}
                        >
                            {t('compositePlan.delete_confirm')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    )
}

export default FrameRegistrationsPanel
